import { isNative } from "@/lib/capacitor";
import { playMessageSound } from "@/lib/sound";
import {
  pushActive,
  pushConfigured,
  requestPushPermission,
  setPushSubscribed,
} from "@/lib/push";

// Local notifications for inbound client messages.
//
// Two backends behind one API: @capacitor/local-notifications inside the
// native shells, and the browser Notification API on the web. Both are
// *local* — they are raised by this app while it is running, so they only
// fire while the webview is alive; iOS suspends a backgrounded webview after
// a short while, which kills the socket.
//
// OneSignal push (`push.ts`) covers exactly that gap, so when it is configured
// and the device is subscribed this module steps aside: the permission prompt
// and the on/off switch are routed through OneSignal, and no local
// notification is raised for a message the server is already pushing. Without
// a OneSignal app id everything below behaves as it always did.

const ENABLED_KEY = "mandoob:notifications";
const ANDROID_CHANNEL_ID = "messages";
// Bundled copy of public/wa-notification.mp3, converted to wav: iOS refuses to
// play mp3 notification sounds, and Android wants the file in res/raw. See
// docs/PUSH.md for where it goes in each native project.
const NOTIFICATION_SOUND = "wa_notification.wav";

export type NotificationPermission =
  | "granted"
  | "denied"
  | "prompt"
  | "unsupported";

type LocalNotificationsModule = typeof import("@capacitor/local-notifications");

let nativeModule: Promise<LocalNotificationsModule | null> | null = null;

function loadNative(): Promise<LocalNotificationsModule | null> {
  if (!isNative()) return Promise.resolve(null);
  // Dynamic so the plain-web bundle never pulls the plugin in.
  nativeModule ??= import("@capacitor/local-notifications").catch(() => null);
  return nativeModule;
}

function webSupported(): boolean {
  return typeof window !== "undefined" && "Notification" in window;
}

// --- User preference -------------------------------------------------------
// Separate from the OS permission: the driver can mute in-app without
// revoking the system permission. Defaults to on, so a driver who grants the
// OS prompt starts receiving messages immediately.

export function notificationsEnabled(): boolean {
  try {
    return localStorage.getItem(ENABLED_KEY) !== "off";
  } catch {
    return true;
  }
}

export function setNotificationsEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(ENABLED_KEY, enabled ? "on" : "off");
  } catch {
    // storage unavailable — preference is per-session only
  }
  // Muting in-app must also stop server pushes, which this device would
  // otherwise keep receiving while the app is closed.
  if (pushConfigured()) void setPushSubscribed(enabled);
}

// --- OS permission ---------------------------------------------------------

export async function checkNotificationPermission(): Promise<NotificationPermission> {
  const native = await loadNative();
  if (native) {
    try {
      const { display } = await native.LocalNotifications.checkPermissions();
      return display === "granted"
        ? "granted"
        : display === "denied"
          ? "denied"
          : "prompt";
    } catch {
      return "unsupported";
    }
  }
  if (!webSupported()) return "unsupported";
  const state = Notification.permission;
  return state === "granted"
    ? "granted"
    : state === "denied"
      ? "denied"
      : "prompt";
}

/**
 * Must be called from a user gesture — iOS and Safari both ignore or
 * permanently deny a prompt raised on page load.
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  // OneSignal owns the prompt when push is configured: its request both asks
  // the OS and registers the push subscription. The permission it grants is
  // the same OS permission the checks above read back.
  if (pushConfigured()) {
    await requestPushPermission();
    return checkNotificationPermission();
  }

  const native = await loadNative();
  if (native) {
    try {
      const { display } = await native.LocalNotifications.requestPermissions();
      if (display === "granted") await ensureAndroidChannel();
      return display === "granted"
        ? "granted"
        : display === "denied"
          ? "denied"
          : "prompt";
    } catch {
      return "unsupported";
    }
  }
  if (!webSupported()) return "unsupported";
  try {
    const state = await Notification.requestPermission();
    return state === "granted" ? "granted" : "denied";
  } catch {
    return "unsupported";
  }
}

async function ensureAndroidChannel(): Promise<void> {
  const native = await loadNative();
  if (!native) return;
  try {
    // No-op on iOS. Importance 4 = shows a heads-up banner.
    await native.LocalNotifications.createChannel({
      id: ANDROID_CHANNEL_ID,
      name: "Messages",
      importance: 4,
      visibility: 1,
      // Android 8+ takes the sound from the channel, not from the individual
      // notification — and it is frozen once the channel exists, so changing
      // the chime later means shipping a new channel id.
      sound: NOTIFICATION_SOUND,
    });
  } catch {
    // Android-only API, or channel already exists.
  }
}

// --- Raising a notification ------------------------------------------------

/** Notification ids must be 32-bit ints; one slot per conversation so a
 *  chatty client replaces its own notification instead of stacking. */
function idForConversation(conversationId: string): number {
  let hash = 0;
  for (let i = 0; i < conversationId.length; i += 1) {
    hash = (hash * 31 + conversationId.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % 2_147_483_647;
}

let onOpen: ((conversationId: string) => void) | null = null;

/** Registers where a tapped notification should navigate. */
export function setNotificationOpenHandler(
  handler: (conversationId: string) => void,
): void {
  onOpen = handler;
}

let tapListenerBound = false;

/** Idempotent: binds the native tap listener once the shell is ready. */
export async function initNotifications(): Promise<void> {
  const native = await loadNative();
  if (!native || tapListenerBound) return;
  tapListenerBound = true;
  try {
    await ensureAndroidChannel();
    await native.LocalNotifications.addListener(
      "localNotificationActionPerformed",
      (action) => {
        const conversationId = action.notification.extra?.conversationId;
        if (typeof conversationId === "string") onOpen?.(conversationId);
      },
    );
  } catch {
    // Listener unavailable — notifications still display, they just
    // won't deep-link on tap.
  }
}

export interface MessageNotification {
  conversationId: string;
  title: string;
  body: string;
}

export async function showMessageNotification(
  input: MessageNotification,
): Promise<void> {
  if (!notificationsEnabled()) return;
  // OneSignal is delivering this same message from the server — raising a
  // local copy would show the driver two banners.
  if (pushActive()) return;
  if ((await checkNotificationPermission()) !== "granted") return;

  const native = await loadNative();
  if (native) {
    try {
      await native.LocalNotifications.schedule({
        notifications: [
          {
            id: idForConversation(input.conversationId),
            title: input.title,
            body: input.body,
            channelId: ANDROID_CHANNEL_ID,
            // iOS, and Android 7 and below, read it from here.
            sound: NOTIFICATION_SOUND,
            extra: { conversationId: input.conversationId },
          },
        ],
      });
    } catch {
      // Scheduling refused (permission revoked mid-session) — drop it.
    }
    return;
  }

  if (!webSupported()) return;
  try {
    const notification = new Notification(input.title, {
      body: input.body,
      // Same tag per conversation: replaces rather than stacks, matching native.
      tag: `conversation:${input.conversationId}`,
      icon: "/icon-192.png",
    });
    // The web Notification API has no custom sound, so the chime is ours to
    // play — the tab is alive whenever this branch runs.
    playMessageSound();
    notification.onclick = () => {
      window.focus();
      onOpen?.(input.conversationId);
      notification.close();
    };
  } catch {
    // Some browsers throw on construction without a service worker.
  }
}
